import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import Cookies from 'universal-cookie';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Joyride from 'react-joyride';
import TourTooltip from '../components/TourTooltip';
import useApi from '../hooks/useApi';
import { putTourCompleted } from '../utils/CommonMethods';
import {
  evaluateMigrationDialogState,
  FIVE_YEAR_MIGRATION_DIALOG_SEEN_KEY,
  getMigrationDialogSeenKey,
  PREMIUM_MIGRATION_DIALOG_SEEN_KEY
} from '../utils/migrationDialogDecision';
import {
  isPremiumMigrationDashboardPath,
  resolveMigrationDashboardPath
} from '../utils/premiumMigrationEligibility';
import locationImage from '../assets/images/add-location.png';
import tableSettingImage from '../assets/images/table-setting.png';
import dataFilterImage from '../assets/images/data-filter.png';
import globalSearchImage from '../assets/images/global-search.png';
import dashboardImage from '../assets/images/vlan-configue.svg';

/** Set when tour is launched from ?startTour=true so FIRST_LOGIN_TOUR_STATUS runs on first Skip/Next. */
const FIRST_LOGIN_TOUR_API_PENDING_KEY = 'firstLoginTourStatusOnTourFinish';
// The hierarchy dropdown Popper uses z-index 9991, so Joyride must sit above it
const TOUR_Z_INDEX = 10000;

function getDashboardMenuTarget() {
  try {
    if (typeof localStorage === 'undefined') return '#Dashboard-mspLevel';

    const scopeId = localStorage.getItem('scopeId');

    if (scopeId === '1') return '#DashboardOrg-parOrg';
    if (scopeId === '2') return '#LocDashboard-loc';
    return '#Dashboard-mspLevel';
  } catch (_) {
    return '#Dashboard-mspLevel';
  }
}

/**
 * Migrated users who already have organizations at login should not see the
 * free-trial add-device dialog after the tour. Users routed to newLogin (no orgs)
 * keep `userHadOrganizationsAtLogin === 'false'` so they still see the dialog.
 */
export function shouldSkipFreeTrialModal() {
  if (localStorage.getItem('muiAccountStatus') !== 'MigratedUser') {
    return false;
  }
  return localStorage.getItem('userHadOrganizationsAtLogin') !== 'false';
}

function setFirstLoginTourApiPendingForTourFinish() {
  try {
    sessionStorage.setItem(FIRST_LOGIN_TOUR_API_PENDING_KEY, '1');
  } catch (_) {}
}

function persistFirstLoginTourIfPendingFromStartTourUrl() {
  let pending = false;
  try {
    pending = sessionStorage.getItem(FIRST_LOGIN_TOUR_API_PENDING_KEY) === '1';
    if (pending) {
      sessionStorage.removeItem(FIRST_LOGIN_TOUR_API_PENDING_KEY);
    }
  } catch (_) {
    return;
  }
  if (!pending) return;
  putTourCompleted().catch((e) => {
    console.error('[FIRST_LOGIN_TOUR_STATUS]', e);
  });
}

/**
 * Tour steps configuration for the Dashboard guided tour
 */
const tourSteps = [
  {
    target: '#org-dropdown-paper-Org',
    title: 'Tree view',
    isNew: true,
    image: locationImage,
    description: 'The hierarchy tree shows your network from top to bottom. Use the hierarchy tree to move between organizations, locations, and AP groups to manage your network at every level.',
    placement: 'right',
    disableBeacon: true,
    styles: {
      options: { overlayColor: 'transparent' },
      spotlight: { backgroundColor: 'transparent' },
    },
  },
  {
    target: '#global-search-popup',
    width: '609px',
    leftImage: globalSearchImage,
    isNew: true,
    title: 'Global Search',
    description: 'Now you can easily search across your network. Enter a keyword, then filter by devices, clients, or features to quickly locate anything in your network.',
    placement: 'left',
    disableBeacon: true,
    styles: {
      options: { overlayColor: 'transparent' },
      spotlight: { backgroundColor: 'transparent' },
    },
  },
  {
    target: '#Dashboard-mspLevel',
    width: '237px',
    imageHeight: 'auto',
    isNew: true,
    image: dashboardImage,
    background: 'bluegrey.light',
    title: 'All New Dashboard',
    description: 'The new Dashboard gives you a real-time overview of network health, device status, and key metrics so you can spot issues and take action quickly.',
    placement: 'bottom',
    disableBeacon: true,
    
  },
  {
    target: '#breadcrumb-container',
    title: 'New Breadcrumb Navigation',
    isNew: true,
    description: 'Breadcrumbs show where you are in the network and let you move back to other levels anytime. Click any breadcrumb to return to an organization, location, or group without losing your place.',
    placement: 'bottom',
    disableBeacon: true,
    disableScrolling: true,
  },
  {
    target: '#dataGridFilterDrawerBody',
    title: 'Data Filter',
    width: '670px',
    isNew: true,
    leftImage: dataFilterImage,
    description: 'Customize your data view with dynamic filters to quickly narrow results by information that matters most to you.',
    placement: 'left',
    disableBeacon: true,
    
  },
  {
    target: '#dataGridrowSettingsBox',
    title: 'Column Settings',
    width: '670px',
    isNew: true,
    leftImage: tableSettingImage,
    description: 'Use table settings to show or hide columns and adjust rows so you can view the data that’s most important to you.',
    placement: 'left',
    disableBeacon: true,
  },
  {
    target: '#dataGridrowSettingsBox',
    title: 'Row Settings',
    width: '670px',
    isNew: true,
    leftImage: tableSettingImage,
    description: 'Use table settings to show or hide columns and adjust rows so you can view the data that’s most important to you.',
    placement: 'left',
    disableBeacon: true,
  },
];


/**
 * Custom hook for managing the Dashboard guided tour
 * 
 * @param {boolean} isCheckingOrgs - Whether the component is still checking organizations
 * @returns {Object} Tour state and component
 */
export default function useDashboardTour(isCheckingOrgs) {
  const cookies = new Cookies();
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [runTour, setRunTour] = useState(false);
  const [tourStepIndex, setTourStepIndex] = useState(0);
  const [showFreeTrialModal, setShowFreeTrialModal] = useState(false);
  const [interStepLoading, setInterStepLoading] = useState(false);
  const [dashboardMenuTarget, setDashboardMenuTarget] = useState(getDashboardMenuTarget);
  const resolvedTourSteps = useMemo(
    () => tourSteps.map((step, index) => (index === 2 ? { ...step, target: dashboardMenuTarget } : step)),
    [dashboardMenuTarget]
  );

  const shouldDeferTourForPremiumMigration = useCallback(async () => {
    try {
      const userId = cookies.get('_Id');
      if (!userId) {
        return false;
      }

      const migrationDialogSeenKey = getMigrationDialogSeenKey(PREMIUM_MIGRATION_DIALOG_SEEN_KEY, userId);
      const fiveYearMigrationDialogSeenKey = getMigrationDialogSeenKey(
        FIVE_YEAR_MIGRATION_DIALOG_SEEN_KEY,
        userId
      );
      const alreadySeenPremiumDialog = migrationDialogSeenKey
        ? localStorage.getItem(migrationDialogSeenKey) === '1'
        : true;
      const alreadySeenFiveYearDialog = fiveYearMigrationDialogSeenKey
        ? localStorage.getItem(fiveYearMigrationDialogSeenKey) === '1'
        : true;

      if (alreadySeenPremiumDialog && alreadySeenFiveYearDialog) {
        return false;
      }

      const migrationState = await evaluateMigrationDialogState();

      if (migrationState.markPremiumSeenBecauseTourCompleted && migrationDialogSeenKey) {
        localStorage.setItem(migrationDialogSeenKey, '1');
      }

      if (
        migrationState.markFiveYearSeenBecauseTourCompleted &&
        fiveYearMigrationDialogSeenKey
      ) {
        localStorage.setItem(fiveYearMigrationDialogSeenKey, '1');
      }

      return (
        (migrationState.showPremiumDialog && !alreadySeenPremiumDialog) ||
        (migrationState.showFiveYearDialog && !alreadySeenFiveYearDialog)
      );
    } catch (_) {
      return false;
    }
  }, [cookies]);

  const clickColumnSettingsTab = useCallback(() => {
    const columnSettingsTab = document.getElementById('dataGridSettingsTab-columns');
    if (columnSettingsTab) {
      columnSettingsTab.click();
      return true;
    }
    return false;
  }, []);
  /**
   * Opens the organisation dropdown programmatically
   */
  const openOrganisationDropdown = useCallback(() => {
    const popper = document.getElementById('org-dropdown-paper-Org');
    if (popper) {
      const rect = popper.getBoundingClientRect();
      if (rect.height > 0 && rect.width > 0) {
        return true;
      }
    }

    const orgDropdownButton = document.getElementById('org-dropdown-button-Org');
    if (orgDropdownButton) {
      orgDropdownButton.click();
      return true;
    }
    return false;
  }, []);

  /**
   * Checks if the organisation dropdown is open
   */
  const isOrganisationDropdownOpen = useCallback(() => {
    const popper = document.getElementById('org-dropdown-paper-Org');
    if (popper) {
      const rect = popper.getBoundingClientRect();
      return rect.height > 0 && rect.width > 0;
    }
    return false;
  }, []);

  /**
   * Closes the organisation dropdown
   */
  const closeOrganisationDropdown = useCallback(() => {
    const orgDropdownButton = document.getElementById('org-dropdown-button-Org');
    if (orgDropdownButton) {
      const popper = document.getElementById('org-dropdown-paper-Org');
      if (popper && popper.getBoundingClientRect().height > 0) {
        orgDropdownButton.click();
      }
    }
  }, []);

  /**
   * Opens the global search popup
   */
  const openGlobalSearch = useCallback(() => {
    // Find the search input box by looking for elements with the search placeholder text
    // The search box is a clickable div that contains "Search (IP, MAC, serial#"
    const allElements = document.querySelectorAll('div');
    for (const element of allElements) {
      const text = element.textContent || '';
      const style = window.getComputedStyle(element);
      if (
        text.includes('Search (IP, MAC, serial#') && 
        style.cursor === 'pointer' &&
        element.querySelector('svg')
      ) {
        element.click();
        return true;
      }
    }
    return false;
  }, []);

  
  /**
   * Closes the global search popup
   */
  const closeGlobalSearch = useCallback(() => {
    const searchBackdrop = document.getElementById('search-backdrop');
    if (searchBackdrop) {
      searchBackdrop.click();
    }
  }, []);

  /**
   * Selects the first organization, then selects the first location from its children.
   * Uses robust retries, expansion, and real click events to ensure selection.
   */
  const selectFirstLocation = useCallback(() => {
    return new Promise((resolve) => {
      // Open the organization dropdown
      if (!openOrganisationDropdown()) {
        resolve(false);
        return;
      }

      // Give up after a reasonable number of animation frames to avoid stalling the tour
      let attempts = 0;
      const maxAttempts = 200; // ~3s at 60fps

      // Helper function to synthesize a reliable click
      const reliableClick = (el) => {
        try {
          el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        } catch (_) {}
        const down = new MouseEvent('mousedown', { bubbles: true, cancelable: true, view: window });
        const up = new MouseEvent('mouseup', { bubbles: true, cancelable: true, view: window });
        const click = new MouseEvent('click', { bubbles: true, cancelable: true, view: window });
        try { el.dispatchEvent(down); } catch (_) {}
        try { el.dispatchEvent(up); } catch (_) {}
        try { el.dispatchEvent(click); } catch (_) {}
      };

      // Helper function to find locations within an organization
      const findLocationsInOrg = (orgTreeItem, resolveFn, retryFn) => {
        // Find all location children of this organization
        const locationItems = [];
        
        // Get all nested tree items (children of the organization)
        const nestedItems = orgTreeItem.querySelectorAll('[role="treeitem"]');
        
        for (const nestedItem of nestedItems) {
          // Check if this nested item is a direct child of the organization
          const parentItem = nestedItem.parentElement?.closest('[role="treeitem"]');
          if (parentItem === orgTreeItem) {
            // This is a direct child (location) of the organization
            const locationClickable =
              nestedItem.querySelector('[id^="org-dropdown-tree-item-typography-"]') ||
              nestedItem.querySelector('.MuiTreeItem-label') ||
              nestedItem;
            if (locationClickable) {
              locationItems.push(locationClickable);
            }
          }
        }
        
        // Select the first location (index 0)
        if (locationItems.length >= 1) {
          reliableClick(locationItems[0]);
          // Ensure the organization dropdown is closed after selection to avoid overlay blocking next targets
          closeOrganisationDropdown();
          resolveFn(true);
        } else {
          // No locations found, retry
          requestAnimationFrame(retryFn);
        }
      };

      // Wait for dropdown to open, then find first organization and its 2nd location
      const findAndClickSecondLocation = () => {
        attempts++;
        if (attempts > maxAttempts) {
          // Could not find a location reliably; proceed with the tour instead of getting stuck
          try { closeOrganisationDropdown(); } catch (_) {}
          resolve(true);
          return;
        }
        const popper = document.getElementById('org-dropdown-paper-Org');
        if (popper && popper.getBoundingClientRect().height > 0) {
          // Find all typography elements
          const allTypographyElements = popper.querySelectorAll('[id^="org-dropdown-tree-item-typography-"]');
          
          // First, find the first organization (not "All Organizations")
          let firstOrgElement = null;
          let firstOrgTreeItem = null;
          
          for (const element of allTypographyElements) {
            const text = element.textContent || '';
            // Skip "All Organizations"
            if (text && text.trim() !== 'All Organizations' && !text.includes('All Organizations')) {
              const treeItem = element.closest('[role="treeitem"]');
              if (treeItem) {
                // Check if this is a top-level organization (direct child of "All Organizations")
                const parentGroup = treeItem.parentElement;
                const isTopLevel = parentGroup && parentGroup.getAttribute('role') === 'group';
                
                  // This is the first organization
                  firstOrgElement = element;
                  firstOrgTreeItem = treeItem;
                  break;
              }
            }
          }
          
          if (firstOrgTreeItem) {
            // Check if the organization has visible children
            const orgChildren = firstOrgTreeItem.querySelectorAll('[role="treeitem"]');
            
            // Find the expand icon in the iconContainer - look for SVG in .MuiTreeItem-iconContainer
            const iconContainer = firstOrgTreeItem.querySelector('.MuiTreeItem-iconContainer');
            const expandIcon = iconContainer ? iconContainer.querySelector('svg') : null;
            
            // Check if organization is collapsed (has expand icon and no visible children)
            if (expandIcon && orgChildren.length === 0) {
              // Organization is collapsed, click the iconContainer or dispatch click event on SVG
              // Try clicking the iconContainer first (it's usually the clickable element)
              if (iconContainer) {
                reliableClick(iconContainer);
              } else if (expandIcon) {
                reliableClick(expandIcon);
              }
              
              // Wait for expansion, then find locations
              requestAnimationFrame(() => {
                findLocationsInOrg(firstOrgTreeItem, resolve, findAndClickSecondLocation);
              });
            } else if (orgChildren.length > 0) {
              // Organization is already expanded, find locations directly
              findLocationsInOrg(firstOrgTreeItem, resolve, findAndClickSecondLocation);
            } else {
              // No children found and no expand icon, retry
              requestAnimationFrame(findAndClickSecondLocation);
            }
          } else {
            // Organization not found, retry
            requestAnimationFrame(findAndClickSecondLocation);
          }
        } else {
          // Dropdown not ready, retry
          requestAnimationFrame(findAndClickSecondLocation);
        }
      };

      // Start immediately
      findAndClickSecondLocation();
    });
  }, [openOrganisationDropdown, closeOrganisationDropdown]);


  /**
   * Navigates to Wi-Fi schedules page
   */
  const navigateToWiFiSchedules = useCallback(() => {
    navigate('/Wi-Fi/schedules');
    return true;
  }, [navigate]);

  

  /**
   * Opens the table settings drawer and ensures Column Settings tab is selected
   */
  const openTableSettings = useCallback(() => {
    // First, close the filter drawer if it's open
    const filterDrawer = document.getElementById('dataGridFilterDrawerBodyTop');
    if (filterDrawer) {
      const filterCloseBtn = document.getElementById('dataGridFilterDrawerCloseBtn');
      if (filterCloseBtn) {
        filterCloseBtn.click();
      }
    }
    
    // Find the settings button and click it
    const settingsButton = document.getElementById('dataGridSettingsBtn');
    if (settingsButton) {
      settingsButton.click();
      return true;
    }
    return false;
  }, []);

  /**
   * Checks if the settings drawer is open and Column Settings tab is selected
   */
  const isColumnSettingsTabAvailable = useCallback(() => {
    const settingsDrawer = document.getElementById('dataGridrowSettingsBox');
    if (settingsDrawer) {
      const rect = settingsDrawer.getBoundingClientRect();
      if (rect.height > 0 && rect.width > 0) {
        // Check if Column Settings tab is selected
        return true;
      }
    }
    return false;
  }, []);

  /**
   * Clicks the Row Settings tab to switch to it
   */
  const clickRowSettingsTab = useCallback(() => {
    const rowSettingsTab = document.getElementById('dataGridSettingsTab-rows');
    if (rowSettingsTab) {
      rowSettingsTab.click();
      return true;
    }
    return false;
  }, []);

  /**
   * Checks if the Row Settings box is available and visible
   */
  const isRowSettingsBoxAvailable = useCallback(() => {
    const rowSettingsBox = document.getElementById('dataGridrowSettingsBox');
    if (rowSettingsBox) {
      const rect = rowSettingsBox.getBoundingClientRect();
      return rect.height > 0 && rect.width > 0;
    }
    return false;
  }, []);

  /**
   * Checks if breadcrumbs are available on the page
   */
  const areBreadcrumbsAvailable = useCallback(() => {
    // Look for breadcrumb container
    const breadcrumbContainer = document.getElementById('breadcrumb-container');
    if (breadcrumbContainer) {
      const rect = breadcrumbContainer.getBoundingClientRect();
      const isVisible = rect.height > 0 && rect.width > 0 && 
                       rect.top >= 0 && 
                       rect.left >= 0 &&
                       rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
                       rect.right <= (window.innerWidth || document.documentElement.clientWidth);
      return isVisible;
    }
    return false;
  }, []);

  /**
   * Starts the tour by opening the organisation dropdown and initializing the tour
   */
  const beginTourRun = useCallback(() => {
    setDashboardMenuTarget(getDashboardMenuTarget());

    // Open the dropdown and start the tour.
    // Retry is bounded so first-login timing races do not loop forever.
    if (!openOrganisationDropdown()) {
      setRunTour(true);
      setTourStepIndex(0);
      return;
    }

    let attempts = 0;
    const maxAttempts = 180; // ~3s at 60fps

    const checkAndStartTour = () => {
      attempts += 1;

      if (isOrganisationDropdownOpen()) {
        setRunTour(true);
        setTourStepIndex(0);
        return;
      }

      // Re-try opening periodically in case an overlay blocked the initial click.
      if (attempts % 20 === 0) {
        openOrganisationDropdown();
      }

      if (attempts < maxAttempts) {
        requestAnimationFrame(checkAndStartTour);
        return;
      }

      // Fallback: start anyway and allow step watchdog to recover UI.
      setRunTour(true);
      setTourStepIndex(0);
    };

    requestAnimationFrame(checkAndStartTour);
  }, [openOrganisationDropdown, isOrganisationDropdownOpen]);

  const handleMigrationBeforeTourStart = useCallback(async () => {
    const pathname = window.location.pathname;
    const shouldDefer = await shouldDeferTourForPremiumMigration();
    if (!shouldDefer) {
      return true;
    }
    if (!isPremiumMigrationDashboardPath(pathname)) {
      navigate(resolveMigrationDashboardPath(pathname));
    }
    return false;
  }, [navigate, shouldDeferTourForPremiumMigration]);

  const startTour = useCallback(() => {
    void (async () => {
      if (!(await handleMigrationBeforeTourStart())) {
        return;
      }
      beginTourRun();
    })();
  }, [beginTourRun, handleMigrationBeforeTourStart]);


  /**
   * Handles Joyride tour callbacks
   */
  const handleJoyrideCallback = useCallback((data) => {
    const { status, index, action, type } = data;

    if (action === 'next' || action === 'skip') {
      persistFirstLoginTourIfPendingFromStartTourUrl();
    }

    const waitForVisible = (getEl, onReady, maxAttempts = 200) => {
      let attempts = 0;
      let stableFrames = 0;
      let lastWidth = -1;
      let lastHeight = -1;

      const tick = () => {
        attempts += 1;

        const el = getEl();

        if (el) {
          const rect = el.getBoundingClientRect();

          if (rect.width > 0 && rect.height > 0) {
            if (
              Math.round(rect.width) === lastWidth &&
              Math.round(rect.height) === lastHeight
            ) {
              stableFrames++;
            } else {
              stableFrames = 0;
              lastWidth = Math.round(rect.width);
              lastHeight = Math.round(rect.height);
            }

            if (stableFrames >= 2) {
              setTimeout(() => {
                onReady();
              }, 150);

              return;
            }
          }
        }

        if (attempts < maxAttempts) {
          requestAnimationFrame(tick);
        } else {
          console.warn('[Tour] waitForVisible timeout');
          onReady();
        }
      };

      requestAnimationFrame(tick);
    };

    const waitForStableElement = (getEl, onReady, maxAttempts = 240) => {
      let attempts = 0;
      let stableFrames = 0;
      let lastRect = '';

      const tick = () => {
        attempts++;

        const el = getEl();

        if (el) {
          const rect = el.getBoundingClientRect();

          if (rect.width > 0 && rect.height > 0) {
            const currentRect = [
              Math.round(rect.x),
              Math.round(rect.y),
              Math.round(rect.width),
              Math.round(rect.height),
            ].join(',');

            if (currentRect === lastRect) {
              stableFrames++;
            } else {
              stableFrames = 0;
              lastRect = currentRect;
            }

            if (stableFrames >= 2) {
              setTimeout(onReady, 100);
              return;
            }
          }
        }

        if (attempts < maxAttempts) {
          requestAnimationFrame(tick);
        } else {
          onReady();
        }
      };

      requestAnimationFrame(tick);
    };

    // BACK / PREV FLOW
    if (action === 'prev' && index === 1) {
      // Step 1 -> Step 0
      closeGlobalSearch();
      setRunTour(false);

      openOrganisationDropdown();

      waitForVisible(
        () => document.getElementById('org-dropdown-paper-Org'),
        () => {
          setTourStepIndex(0);
          setRunTour(true);
        }
      );

      return;
    }

    if (action === 'prev' && index === 2) {
      // Step 2 -> Step 1
      setRunTour(false);

      openGlobalSearch();

      waitForVisible(
        () => document.getElementById('global-search-popup'),
        () => {
          setTourStepIndex(1);
          setRunTour(true);
        }
      );

      return;
    }

    if (action === 'prev' && index === 3) {
      // Step 3 -> Step 2
      setRunTour(false);

      closeGlobalSearch();
      closeOrganisationDropdown();
      const dashboardTarget = '#Dashboard-mspLevel';
      setDashboardMenuTarget(dashboardTarget);
      navigate('/mspHome/dashboard');

      waitForVisible(
        () => document.querySelector(dashboardTarget),
        () => {
          setRunTour(false);

          requestAnimationFrame(() => {
            setTourStepIndex(2);

            requestAnimationFrame(() => {
              setRunTour(true);
            });
          });
        }
      );

      return;
    }

    if (action === 'prev' && index === 4) {
      // Step 4 -> Step 3
      setRunTour(false);

      const filterCloseBtn = document.getElementById('dataGridFilterDrawerCloseBtn');
      if (filterCloseBtn) {
        filterCloseBtn.click();
      }

      setTimeout(() => {
        setTourStepIndex(3);
        setRunTour(true);
      }, 150);

      return;
    }

    if (action === 'prev' && index === 5) {
      setRunTour(false);

      const settingsCloseBtn = document.getElementById('dataGridSettingsDrawerCloseBtn');
      if (settingsCloseBtn) {
        settingsCloseBtn.click();
      }

      const waitForSettingsDrawerToClose = () => {
        const settingsDrawer = document.getElementById('dataGridSettingsDrawer');

        const isClosed =
          !settingsDrawer ||
          settingsDrawer.getBoundingClientRect().width === 0 ||
          settingsDrawer.getBoundingClientRect().height === 0;

        if (isClosed) {
          const filterBtn = document.getElementById('dataGridFilterBtn');
          filterBtn?.click();

          waitForStableElement(
            () => document.getElementById('dataGridFilterDrawerBody'),
            () => {
              setTourStepIndex(4);
              setRunTour(true);
            }
          );
        } else {
          requestAnimationFrame(waitForSettingsDrawerToClose);
        }
      };

      requestAnimationFrame(waitForSettingsDrawerToClose);
      return;
    }

    if (action === 'prev' && index === 6) {
      // Step 6 -> Step 5
      setRunTour(false);

      clickColumnSettingsTab();

      setTimeout(() => {
        setTourStepIndex(5);
        setRunTour(true);
      }, 150);

      return;
    }

    // When clicking next on step 1 (index 0), prepare for step 2
    if (action === 'next' && index === 0) {
      // Close the organisation dropdown first
      closeOrganisationDropdown();
      
      // Pause the tour temporarily
      setRunTour(false);
      
      // Open the global search
      openGlobalSearch();
      
      // Wait for the popup to be fully rendered, then resume tour at step 2
      const checkAndResumeTour = () => {
        const searchPopup = document.getElementById('global-search-popup');
        if (searchPopup) {
          const rect = searchPopup.getBoundingClientRect();
          if (rect.height > 0 && rect.width > 0) {
            // Popup is visible, resume tour at step 2
            setTourStepIndex(1);
            setRunTour(true);
          } else {
            // Popup not visible yet, check again
            requestAnimationFrame(checkAndResumeTour);
          }
        } else {
          // Popup not found, try opening again and wait
          openGlobalSearch();
          requestAnimationFrame(checkAndResumeTour);
        }
      };
      
      // Start checking immediately
      requestAnimationFrame(checkAndResumeTour);
      
      return; // Prevent default progression
    }
    
    // When clicking next on step 2 (index 1), prepare for step 3
    if (action === 'next' && index === 1) {
      // Close the global search popup first
      closeGlobalSearch();
      
      // Pause the tour temporarily
      setRunTour(false);
      
      // Wait for the Dashboard menu item to be available, then resume tour at step 3
      const checkAndResumeTour = () => {
        const dashboardMenuItem = document.querySelector(dashboardMenuTarget);
        if (dashboardMenuItem) {
          const rect = dashboardMenuItem.getBoundingClientRect();
          if (rect.height > 0 && rect.width > 0) {
            // Menu item is visible, resume tour at step 3
            setTourStepIndex(2);
            setRunTour(true);
          } else {
            // Menu item not visible yet, check again
            requestAnimationFrame(checkAndResumeTour);
          }
        } else {
          // Menu item not found, check again
          requestAnimationFrame(checkAndResumeTour);
        }
      };
      
      // Start checking immediately
      requestAnimationFrame(checkAndResumeTour);
      
      return; // Prevent default progression
    }
    
    // When clicking next on step 3 (index 2), prepare for step 4
    if (action === 'next' && index === 2) {
      // Pause the tour temporarily
      setRunTour(false);
      // Show loader while we navigate and wait for schedules page readiness
      setInterStepLoading(true);
      // Direct approach: determine a valid org/location via API and set context, then navigate to schedules.
      (async () => {
        try {
          const orgTreeResult = await useApi("GET_ORG_CHILD_DETAILS", {}, {}, ["_Id", "networkId"]);
          const orgs = orgTreeResult?.info || [];
          let targetOrgId = null;
          let targetOrgName = null;
          let targetLocId = null;
          let targetLocName = null;
          // Pick first org with a location; otherwise pick first org and no location
          for (const org of orgs) {
            const children = Array.isArray(org.children) ? org.children : [];
            if (children.length > 0) {
              targetOrgId = org.id || org.orgId || org._id || org.OrgId;
              targetOrgName = org.orgName || org.name || '';
              const firstLoc = children[0];
              targetLocId = firstLoc.id || firstLoc.networkId || firstLoc._id;
              targetLocName = firstLoc.networkName || firstLoc.name || '';
              break;
            }
            if (targetOrgId == null) {
              targetOrgId = org.id || org.orgId || org._id || org.OrgId;
              targetOrgName = org.orgName || org.name || '';
            }
          }
          if (targetOrgId) {
            try { localStorage.setItem("OrgId", String(targetOrgId)); } catch(_) {}
          }
          if (targetLocId) {
            try { localStorage.setItem("networkId", String(targetLocId)); } catch(_) {}
            try { localStorage.setItem("currentNetwork", String(targetLocId)); } catch(_) {}
            try { localStorage.setItem("selectedType", JSON.stringify({ id: String(targetLocId), label: targetLocName || 'Location' })); } catch(_) {}
          } else {
            // Clear location context to avoid stale selection if none found
            try { localStorage.removeItem("networkId"); } catch(_) {}
            try { localStorage.removeItem("currentNetwork"); } catch(_) {}
            try { localStorage.removeItem("selectedType"); } catch(_) {}
          }
        } catch (e) {
          // Proceed anyway
        }
        // Navigate directly to schedules
        navigateToWiFiSchedules();
        // Wait for schedules page readiness, then resume tour at step 4
        let attempts = 0;
        const maxAttempts = 600; // ~10s
        const waitReady = () => {
          attempts++;
          const onSchedules = window.location.pathname.includes('/Wi-Fi/schedules') || window.location.pathname.includes('/schedules');
          if (onSchedules) {
            const breadcrumbElement =
              document.getElementById('breadcrumb-container') ||
              document.querySelector('[data-breadcrumb]') ||
              document.querySelector('nav[aria-label="breadcrumb"]') ||
              document.getElementById('dataGridFilterBtn');
            if (breadcrumbElement) {
              setTimeout(() => {
                setInterStepLoading(false);

                waitForStableElement(
                  () => document.getElementById('breadcrumb-container') ||
                     document.querySelector('[data-breadcrumb]') ||
                     document.querySelector('nav[aria-label="breadcrumb"]'),
                  () => {
                    setRunTour(false);

                    requestAnimationFrame(() => {
                      setTourStepIndex(3);

                      requestAnimationFrame(() => {
                        setRunTour(true);

                        setTimeout(() => {
                          window.dispatchEvent(new Event("resize"));
                        }, 50);
                      });
                    });
                  }
                );
              }, 100);
              return;
            }
          }
          if (attempts < maxAttempts) {
            requestAnimationFrame(waitReady);
          } else {
            // Give up waiting but still progress
            setInterStepLoading(false);
            setTourStepIndex(3);
            setRunTour(true);
          }
        };
        requestAnimationFrame(waitReady);
      })();
      
      return; // Prevent default progression
    }
    
    // When clicking next on step 4 (index 3), prepare for step 5
    if (action === 'next' && index === 3) {
      // Pause the tour temporarily
      setRunTour(false);
      
      let filterCheckAttempts = 0;
      const maxFilterChecks = 100; // Maximum attempts (about 1.6 seconds at 60fps)
      
      // Try to open the filter popup
      const tryOpenFilter = () => {
        const filterButton = document.getElementById('dataGridFilterBtn');
        if (filterButton) {
          filterButton.click();
          return true;
        }
        return false;
      };
      
      // First attempt to open filter
      const filterOpened = tryOpenFilter();
      
      if (filterOpened) {
        // Wait for filter drawer to open, then resume tour at step 5
        const waitForFilterDrawer = () => {
          filterCheckAttempts++;
          
          const filterDrawer = document.getElementById('dataGridFilterDrawerBody');
          if (filterDrawer) {
            const rect = filterDrawer.getBoundingClientRect();
            const isVisible = rect.height > 0 && rect.width > 0 && 
                             rect.top >= 0 && 
                             rect.left >= 0;
            
            if (isVisible) {
              // Filter drawer is open and visible, resume tour at step 5
              // Add a small delay to ensure React has fully rendered
              setTimeout(() => {
                setTourStepIndex(4);
                setRunTour(true);
              }, 100);
              return;
            } else if (filterCheckAttempts < maxFilterChecks) {
              // Drawer element exists but not visible yet, check again
              requestAnimationFrame(waitForFilterDrawer);
              return;
            }
          } else if (filterCheckAttempts < maxFilterChecks) {
            // Filter drawer not found yet, check again
            requestAnimationFrame(waitForFilterDrawer);
            return;
          }
          
          // If we've exhausted attempts, try clicking again and resume anyway
          if (filterCheckAttempts >= maxFilterChecks) {
            console.warn('[Tour] Filter drawer check timeout, attempting to resume tour');
            // Try clicking the button one more time
            tryOpenFilter();
            setTimeout(() => {
              setTourStepIndex(4);
              setRunTour(true);
            }, 200);
          }
        };
        
        // Start checking for filter drawer after a short delay
        setTimeout(() => {
          requestAnimationFrame(waitForFilterDrawer);
        }, 200);
      } else {
        // Filter button not found, try to continue anyway after a delay
        console.warn('[Tour] Filter button not found, attempting to continue...');
        setTimeout(() => {
          // Try one more time to find and click the button
          if (tryOpenFilter()) {
            setTimeout(() => {
              setTourStepIndex(4);
              setRunTour(true);
            }, 300);
          } else {
            // Still not found, continue anyway
            setTourStepIndex(4);
            setRunTour(true);
          }
        }, 200);
      }
      
      return; // Prevent default progression
    }
    
    // When clicking next on step 5 (index 4), prepare for step 6
    if (action === 'next' && index === 4) {
      setRunTour(false);

      const filterCloseBtn = document.getElementById(
        'dataGridFilterDrawerCloseBtn'
      );

      if (filterCloseBtn) {
        filterCloseBtn.click();
      }

      setTimeout(() => {
        if (openTableSettings()) {

          waitForStableElement(
            () => document.getElementById("dataGridrowSettingsBox"),
            () => {
              setTourStepIndex(5);
              setRunTour(true);
            }
          );

        } else {
          setTourStepIndex(5);
          setRunTour(true);
        }
      }, 200);

      return;
    }
    
    // When clicking next on step 6 (index 5), prepare for step 7
    if (action === 'next' && index === 5) {
      // Pause the tour temporarily
      setRunTour(false);
      
      // Click the Row Settings tab
      if (clickRowSettingsTab()) {
        // Wait for Row Settings box to be available, then resume tour at step 7
        waitForStableElement(
            () => document.getElementById("dataGridrowSettingsBox"),
            () => {
                setTourStepIndex(6);
                setRunTour(true);
            }
        );
      } else {
        // Row Settings tab not found, try to continue anyway
        console.warn('Row Settings tab not found, continuing...');
        setTourStepIndex(6);
        setRunTour(true);
      }
      
      return; // Prevent default progression
    }

    // Handle tour completion - check status first
    // When clicking Finish on step 7 (index 6), end the tour
    // This handles the case where continuous={true} and clicking Finish on last step
    if (action === 'next' && index === 6) {
      // This is the last step, end the tour
      setRunTour(false);
      setTourStepIndex(0);
      closeGlobalSearch();
      window.dispatchEvent(new CustomEvent('dashboardTourStateChange', { detail: { status: 'finished' } }));

      const filterCloseBtn = document.getElementById('dataGridSettingsDrawerCloseBtn');
      if (filterCloseBtn) {
          filterCloseBtn.click();
      }

      // Navigate to dashboard first
      navigate('/mspHome/dashboard');
      // Ensure page starts at top after navigating
      setTimeout(() => {
        try {
          window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
        } catch (e) {
          // no-op
        }
      }, 0);
      
      // Open free trial modal when tour finishes (after navigation)
      setTimeout(() => {
        if (!shouldSkipFreeTrialModal()) {
          setShowFreeTrialModal(true);
        }
      }, 300);
      
      return; // Prevent default progression
    }

    if (status === 'finished' || status === 'skipped') {
      setRunTour(false);
      setTourStepIndex(0);
      setInterStepLoading(false);
      window.dispatchEvent(new CustomEvent('dashboardTourStateChange', { detail: { status } }));
      // Close all UI elements that may have been opened during the tour
      // Close organization dropdown (opened at step 0)
      closeOrganisationDropdown();
      // Close search popup if open (opened at step 1)
      closeGlobalSearch();
      // Close filter drawer if open (opened at step 4)
      const filterCloseBtn = document.getElementById('dataGridFilterDrawerCloseBtn');
      if (filterCloseBtn) {
        filterCloseBtn.click();
      }
      // Close settings drawer if open (opened at steps 5-6)
      const settingsCloseBtn = document.getElementById('dataGridSettingsDrawerCloseBtn');
      if (settingsCloseBtn) {
        settingsCloseBtn.click();
      }
      
      // Navigate to dashboard for both finished and skipped
      navigate('/mspHome/dashboard');
      // Ensure page starts at top after navigating
      setTimeout(() => {
        try {
          window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
        } catch (e) {
          // no-op
        }
      }, 0);
      
      // Open free trial modal when tour finishes (only for finished, not skipped)
      if (status === 'finished') {
        // Wait for navigation to complete before showing modal
        setTimeout(() => {
          if (!shouldSkipFreeTrialModal()) {
            setShowFreeTrialModal(true);
          }
        }, 300);
      }
      
      return; // Prevent any further processing
    }
    
  }, [closeOrganisationDropdown, openOrganisationDropdown, openGlobalSearch, closeGlobalSearch, selectFirstLocation, navigate, navigateToWiFiSchedules, areBreadcrumbsAvailable, openTableSettings, isColumnSettingsTabAvailable, clickRowSettingsTab, isRowSettingsBoxAvailable, clickColumnSettingsTab, dashboardMenuTarget]);

  /**
   * Check for startTour query parameter and start the tour
   */
  useEffect(() => {
    const startTourParam = searchParams.get('startTour');
    if (startTourParam !== 'true' || isCheckingOrgs) return;

    const alreadyPersisted = searchParams.get('firstLoginTourStatusSent') === '1';

    if (!alreadyPersisted) {
      setFirstLoginTourApiPendingForTourFinish();
    }

    (async () => {
      if (!(await handleMigrationBeforeTourStart())) {
        return;
      }
      beginTourRun();
    })();

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('startTour');
      next.delete('firstLoginTourStatusSent');
      return next;
    }, { replace: true });
  }, [beginTourRun, handleMigrationBeforeTourStart, isCheckingOrgs, searchParams, setSearchParams]);

  useEffect(() => {
    try {
      if (runTour) {
        sessionStorage.setItem('dashboardTourRunning', '1');
        sessionStorage.setItem(
          'dashboardTourStep',
          String(tourStepIndex)
        );
      } else {
        sessionStorage.removeItem('dashboardTourRunning');
        sessionStorage.removeItem('dashboardTourStep');
      }
    } catch (_) {
      // Ignore storage access failures
    }
  }, [runTour, tourStepIndex]);

  useEffect(() => {
    if (!runTour) return;

    let attempts = 0;
    const maxAttempts = 10;

    const interval = setInterval(() => {
      attempts++;

      if (tourStepIndex === 0) {
        const treePopper = document.getElementById('org-tree-popper-Org1');

        const visible =
          treePopper &&
          treePopper.getBoundingClientRect().height > 0 &&
          treePopper.getBoundingClientRect().width > 0;

        if (!visible) {
          openOrganisationDropdown();
        }
      }

      if (tourStepIndex === 1) {
        const searchPopup = document.getElementById('global-search-popup');

        const visible =
          searchPopup &&
          searchPopup.getBoundingClientRect().height > 0 &&
          searchPopup.getBoundingClientRect().width > 0;

        if (!visible) {
          openGlobalSearch();
        }
      }

      if (attempts >= maxAttempts) {
        clearInterval(interval);
      }
    }, 500);

    return () => clearInterval(interval);
  }, [
    runTour,
    tourStepIndex,
    openOrganisationDropdown,
    openGlobalSearch,
  ]);

  /**
   * Renders the Joyride tour component
   */
  const TourComponent = () => (
    <>
      <Joyride
        steps={resolvedTourSteps}
        run={runTour}
        stepIndex={tourStepIndex}
        continuous={true}
        showProgress={true}
        showSkipButton={true}
        disableOverlay={false}
        disableOverlayClose
        disableCloseOnEsc
        spotlightClicks={false}
        tooltipComponent={TourTooltip}
        callback={handleJoyrideCallback}
        styles={{
          options: {
            primaryColor: '#2c3348',
            zIndex: TOUR_Z_INDEX,
          },
          tooltip: {
            backgroundColor: 'transparent',
          },
          tooltipContainer: {
            zIndex: TOUR_Z_INDEX,
          },
          spotlight: {
            zIndex: TOUR_Z_INDEX - 1,
          },
        }}
      />
      <Backdrop
        open={interStepLoading}
        sx={{ color: '#fff', zIndex: (theme) => Math.max(1600, theme.zIndex.modal + 10) }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );

  return {
    TourComponent,
    runTour,
    setRunTour,
    tourStepIndex,
    setTourStepIndex,
    startTour,
    showFreeTrialModal,
    setShowFreeTrialModal,
  };
}
